import { notification } from 'antd'
import i18next from 'i18next'
import { makeAutoObservable } from 'mobx'

import { setLocalStorageData, STORAGE_KEY } from 'services/localStorage.service'
import { SignboardFormat, SignboardSize } from 'sections/LKSection/modules/OrderModule/types'

import { pvzApi } from '../../api'
import { PvzKeys } from '../../data'
import { TimetableStatus } from '../../types/types'
import { UpdateSignboardData } from '../../types/typesDTO'
import { ViewPvzUiStore } from './ViewPvzUiStore'

const getKey = (key: string) => `${PvzKeys.pvzModule}.api.${key}`

export class ViewPvzApiStore {
  constructor(protected readonly uiStore: ViewPvzUiStore) {
    makeAutoObservable(this)
  }

  async updatePvz(pvzId: number, updatedFields?: string[]) {
    if (!this.uiStore.selectedPvz?.id || !this.uiStore.hashFormState) {
      return false
    }

    const selectedPvz = this.uiStore.selectedPvz
    const actualUrls = await this.uiStore.fileList.processFiles(selectedPvz)
    const areaModel = this.uiStore.areaModel
    const timetable = this.uiStore.timetable
    const features = this.uiStore.features.reduce((acc, feature) => {
      if (feature.checked) {
        acc.push(feature.value)
      }

      return acc
    }, [] as string[])

    if (!actualUrls) {
      return false
    }

    try {
      const result = await pvzApi.updatePvz(
        {
          pvzId,
          description: this.uiStore.description.trim(),
          courierWay: this.uiStore.courierWay.trim(),
          alarmSystem: this.uiStore.alarmSystem.trim(),
          actualUrls,
          dressingRoomsCount: this.uiStore.dressingRoomsCount ?? undefined,
          area: areaModel.isAreaWasChanged ? areaModel.area : undefined,
          warehouseArea: areaModel.warehouseArea,
          timetable: JSON.stringify(timetable.getTimetable(selectedPvz.typePoint, selectedPvz.floorLevel)),
          timetableUpdateReason: timetable.timetableUpdateReason.trim() || undefined,
          racksCount: this.uiStore.racksCount,
          cellsCount: this.uiStore.cellsCount,
          maxCellsCount: this.uiStore.maxCellsCount,
          features,
        },
        updatedFields,
      )
      this.uiStore.getPvzList()

      notification.success({
        message: i18next.t(getKey('updatePvzSuccess'), { count: this.uiStore.selectedPvz.externalId }),
      })

      return result
    } catch (error) {
      notification.error({
        message: i18next.t(getKey('updatePvzError')),
        description: (error as Error).message,
      })
    }
  }

  async switchPvz(pvzId: number) {
    if (pvzId !== this.uiStore.switchedPvzId) {
      try {
        this.uiStore.setIsSwitchPvzLoading(true)
        this.uiStore.setSwitchedPvzId(pvzId)

        const pvz = this.uiStore.getPvz(+pvzId)
        this.uiStore.setSelectedPvz(pvz)

        const result = await pvzApi.switchPvz(pvzId)

        setLocalStorageData(STORAGE_KEY.ACCESS_TOKEN, result.access.token)
        setLocalStorageData(STORAGE_KEY.REFRESH_TOKEN, result.refresh.token)

        return result
      } catch (error) {
        notification.error({
          message: i18next.t(getKey('switchPvzError')),
          description: (error as Error).message,
        })
      } finally {
        this.uiStore.setIsSwitchPvzLoading(false)
      }
    }
  }

  async getTimetableEditStatus(pvzId: number) {
    try {
      const result = await pvzApi.getTimetableEditStatus(pvzId)
      this.uiStore.setTimetableEditStatus(result?.status ?? TimetableStatus.none)
    } catch (error) {
      notification.error({
        message: i18next.t(getKey('getTimetableEditStatusError')),
        description: (error as Error).message,
      })
    }
  }

  async getPvzSignboardList(pvzId: number) {
    try {
      const result = await pvzApi.getPvzSignboard(pvzId)
      this.uiStore.setSignboardData(result)
    } catch (error) {
      this.uiStore.setSignboardData({
        isCheck: false,
        signboardFormat: SignboardFormat.none,
        signboardSize: SignboardSize.none,
        signboardUrls: [],
      })
      notification.error({
        message: i18next.t(getKey('getPvzSignboardListError')),
        description: (error as Error).message,
      })
    }
  }

  async updatePvzSignboard(patch: UpdateSignboardData) {
    try {
      const result = await pvzApi.updatePvzSignboard(patch)
      return result
    } catch (error) {
      notification.error({
        message: i18next.t(getKey('updatePvzSignboardError')),
        description: (error as Error).message,
      })
    }
  }
}
